import type { ColorAttribute } from './helpers';
import { findOptionIgnoringCase } from './variantMappingRepairStrategies/casing';
import { getReplacementAttribute, replacementKey } from './variantMappingRepairStrategies/manual';
import { suggestRenamedAttribute } from './variantMappingRepairStrategies/renamedValues';
import type { AttributeReplacement, RepairContext } from './variantMappingRepairStrategies/types';

export type ValueReplacementConflict = {
    original: ColorAttribute;
    target: ColorAttribute;
    otherOriginals: ColorAttribute[];
};

/** Plan against every original value, including healthy variants and variants that cannot be fully repaired. */
export const buildValueReplacementPlan = (
    attributes: readonly ColorAttribute[],
    context: RepairContext,
    replacements: readonly AttributeReplacement[] = []
) => {
    const values = Array.from(
        new Map(attributes.map((attribute) => [JSON.stringify([attribute.Id, attribute.Label, attribute.Value.Id, attribute.Value.Label]), attribute])).values()
    );
    const candidates = values.map((attribute) => {
        const key = replacementKey(attribute.Id, attribute.Value.Id);
        const option = findOptionIgnoringCase(attribute, context.options, !context.isShopify);
        // An unresolved/deleted option must not claim the identity of a store option at its old position.
        const source = option
            ? replacementKey(option.optionID, attribute.Value.Id)
            : JSON.stringify(['unresolved-option', attribute.Id, attribute.Label, attribute.Value.Id]);
        const selected = replacements.filter((replacement) => replacementKey(replacement.optionID, replacement.valueID) === key);
        const suggestion = selected.length
            ? selected.length === 1
                ? getReplacementAttribute(attribute, selected[0], context.options)
                : null
            : suggestRenamedAttribute(attribute, context);
        return {
            key,
            attribute,
            source,
            suggestion,
            target: suggestion ? replacementKey(suggestion.Id, suggestion.Value.Id) : null,
            // Existing values remain reserved even if another option blocks this variant's repair.
            reserved: option?.values.some((value) => value.valueID === attribute.Value.Id) ? source : null
        };
    });
    const claims = candidates.flatMap(({ source, target, reserved, attribute }) =>
        [target, reserved].flatMap((value) => (value === null ? [] : [{ source, target: value, attribute }]))
    );
    const conflicts = new Set(
        candidates
            .filter(
                (candidate) =>
                    candidate.target !== null &&
                    ((candidate.source !== candidate.target &&
                        claims.some((claim) => claim.target === candidate.target && claim.source !== candidate.source)) ||
                        candidates.some((other) => other.source === candidate.source && other.target !== candidate.target))
            )
            .map(({ key }) => key)
    );
    const conflictDetails = new Map(
        candidates.flatMap((candidate) =>
            conflicts.has(candidate.key) && candidate.suggestion
                ? [
                      [
                          candidate.key,
                          {
                              original: candidate.attribute,
                              target: candidate.suggestion,
                              otherOriginals: Array.from(
                                  new Map(
                                      claims
                                          .filter((claim) => claim.target === candidate.target && claim.source !== candidate.source)
                                          .map((claim) => [claim.source, claim.attribute])
                                  ).entries()
                              )
                                  .sort(([left], [right]) => left.localeCompare(right))
                                  .map(([, attribute]) => attribute)
                          }
                      ] as const
                  ]
                : []
        )
    );
    const suggestions = new Map(candidates.flatMap(({ key, suggestion }) => (suggestion && !conflicts.has(key) ? [[key, suggestion] as const] : [])));
    return { suggestions, conflicts, conflictDetails };
};
